import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api } from "./api";

/** Same categories as the api's `OriginChannel` (attribution-origin.ts),
 *  plus "unknown" — the origin lookup's own failure state (DB hiccup),
 *  distinct from "unattributed" (lookup ran, found nothing). */
export type ApprovalOriginChannel =
  | "meta_ads"
  | "google_ads"
  | "organic"
  | "unattributed"
  | "broken"
  | "unknown";

export interface ApprovalOrigin {
  channel: ApprovalOriginChannel;
  campaignName: string | null;
}

export interface ApprovalListItem {
  id: number;
  projectId: number;
  projectName: string;
  eventType: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  dealValue: number | null;
  currency: string | null;
  createdAt: string;
  gateType: "approval_rule" | "merge_review" | null;
  /** ISO time the purchase was already reported to Meta/Google while it waits
   *  here (not revocable there); null = not reported. */
  capiPresendAt: string | null;
  /** No deciding rule: the rule check failed technically, the event waits for a human. */
  ruleCheckFailed: boolean;
  /** Purchased product(s) — several items arrive comma-joined. */
  productName: string | null;
  /** How many suspect events folded into this one review (≥1). */
  affectedEventCount: number;
  /** ISO timestamp of the most recent folded-in event, if any. */
  lastSeenAt: string | null;
  /** Channel + campaign name of this contact's first attributed event. */
  origin: ApprovalOrigin;
  mergeRisk: {
    reasons: string[];
    triggerKeys: Array<{
      keyType: string;
      value: string;
      visitorCount: number;
    }>;
    /** Strong-key collisions: e.g. two different emails on one visitor. */
    strongKeyConflicts: Array<{
      keyType: string;
      values: string[];
    }>;
  } | null;
  payload: Record<string, unknown>;
}

export interface ApprovalListResponse {
  items: ApprovalListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface ApprovalFilters {
  project?: string;
  eventType?: string;
  gateType?: "approval_rule" | "merge_review";
}

/** Decided case (TASK-209) — shape of GET /admin/approvals/history. */
export interface ApprovalHistoryItem {
  id: number;
  projectId: number;
  projectName: string;
  eventType: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  dealValue: number | null;
  currency: string | null;
  gateType: "approval_rule" | "merge_review";
  productName: string | null;
  decidedAt: string | null;
  result: "approved" | "rejected" | "rejected_after_send";
  decider: { kind: "person" | "rule" | "ai"; label: string; detail?: string };
  delivery: {
    /** false = no report exists for this gate (merge review). */
    applicable: boolean;
    /** Handed over before the decision — information, not a delivery proof. */
    presendAt: string | null;
    meta: HistoryDeliveryState;
    google: HistoryDeliveryState;
  };
}

export interface HistoryDeliveryState {
  status: "delivered" | "failed" | "suppressed" | "none";
  at: string | null;
}

export interface ApprovalHistoryResponse {
  items: ApprovalHistoryItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface ApprovalHistoryFilters {
  project?: string;
  eventType?: string;
  status?: string;
  decision?: string;
}

export const EVENT_TYPES = [
  "PageView",
  "Interaction",
  "Lead",
  "HQLead",
  "WebinarAttendance",
  "Contact",
  "Purchase",
];

export const HISTORY_PAGE_SIZE = 50;

/** The shared `project` URL parameter only counts when it is a plain number; anything else is dropped (no junk to the API). */
export function historyProjectParam(raw: string | null): string | undefined {
  return raw && /^\d+$/.test(raw) ? raw : undefined;
}

/** A page number held back to the real range 1..ceil(total/50), so a stale link or a shrunken list never shows an empty page. */
export function clampHistoryPage(page: number, total: number | undefined): number {
  const wanted = Math.max(1, page);
  if (total === undefined) return wanted;
  return Math.min(wanted, Math.max(1, Math.ceil(total / HISTORY_PAGE_SIZE)));
}

/** Plain text for a failed history request (ApiError carries the server's wording). */
export function historyErrorText(err: unknown): string {
  return err instanceof Error && err.message.trim() ? err.message : "Unbekannter Fehler";
}

export function buildHistoryQuery(
  filters: ApprovalHistoryFilters,
  limit: number,
  offset: number,
): string {
  const params = new URLSearchParams();
  for (const k of ["project", "eventType", "status", "decision"] as const) {
    if (filters[k]) params.set(k, filters[k]);
  }
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  return `?${params.toString()}`;
}

export type KeyStrength = "strong" | "medium" | "weak";

export type ActivitySignal = "new" | "recent" | "stale" | "dormant";

export interface VisitorActivity {
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  eventCount: number;
  daysSinceLastSeen: number | null;
  signal: ActivitySignal;
}

export interface MergeBridge {
  keyType: string;
  value: string;
  originValue: string;
  strength: KeyStrength;
  visitorIds: number[];
}

export interface PreviewResolution {
  id: number;
  droppedKeys: string[];
  keys: Record<string, string>;
  resolution: {
    canonicalVisitorId: number;
    newVisitor: boolean;
    mergeIds: number[];
    wouldMergeCount: number;
  };
  keyCardinalities: Array<{
    keyType: string;
    value: string;
    strength: KeyStrength;
    visitorCount: number;
    suspect: boolean;
  }>;
  involvedVisitors: Array<{
    visitorId: number;
    isCanonical: boolean;
    keys: Array<{
      keyType: string;
      value: string;
      strength: KeyStrength;
      matchesIncoming: boolean;
    }>;
    activity: VisitorActivity | null;
  }>;
  mergeBridges: MergeBridge[];
  bridgeSummary: {
    weakestStrength: KeyStrength | null;
    strongestStrength: KeyStrength | null;
    onlyWeakBridges: boolean;
    suggestedDropKeys: string[] | null;
    suggestedReason: string | null;
  };
}

export function usePreviewResolution(
  id: number,
  droppedKeys: string[],
  enabled: boolean,
) {
  const qs = droppedKeys.length > 0
    ? `?dropKeys=${droppedKeys.map(encodeURIComponent).join(",")}`
    : "";
  return useQuery<PreviewResolution>({
    queryKey: [...BASE_KEY, "preview", id, droppedKeys.slice().sort().join(",")],
    queryFn: () =>
      api<PreviewResolution>(`/admin/approvals/${id}/preview${qs}`),
    staleTime: 5_000,
    enabled: enabled && id > 0,
    retry: 1,
  });
}

/** Full origin block for ONE approval (TASK-58) — richer than the list's
 *  ApprovalOrigin: raw fields + first-contact timestamp, so a "broken"
 *  reading still shows what actually arrived. */
export interface ApprovalOriginDetail {
  channel: ApprovalOriginChannel;
  campaignName: string | null;
  occurredAt: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  dhC: string | null;
  dhAs: string | null;
  dhA: string | null;
}

export function useApprovalOrigin(id: number, enabled: boolean) {
  return useQuery<ApprovalOriginDetail>({
    queryKey: [...BASE_KEY, "origin", id],
    queryFn: () => api<ApprovalOriginDetail>(`/admin/approvals/${id}/origin`),
    staleTime: 10_000,
    enabled: enabled && id > 0,
    retry: 1,
  });
}

/** Anchor for a touchpoint inserted from the approval drawer (TASK-76).
 *  `visitorId: null` covers both non-resolvable states alike — no email key
 *  on the approval, or an email that matches no existing visitor (TASK-62's
 *  known limit). `oldestKnownAt: null` with a resolved visitor means a
 *  brand-new contact with no prior event in Postgres or the BigQuery mirror
 *  — a valid state, not a failure. */
export interface ApprovalTouchpointAnchor {
  visitorId: number | null;
  oldestKnownAt: string | null;
  /** Same bqMirrorEnabled/error contract as the customer detail's bq-count
   *  endpoint (review F1, TASK-76): `false` with no `error` means there was
   *  nothing to check (mirror off for this project) — not a failure. `true`
   *  with `error` set means the mirror IS enabled but the lookup failed, so
   *  oldestKnownAt fell back to Postgres alone and isn't provably complete. */
  bqMirrorEnabled: boolean;
  error?: "bq_query_failed";
}

export function useApprovalTouchpointAnchor(id: number, enabled: boolean) {
  return useQuery<ApprovalTouchpointAnchor>({
    queryKey: [...BASE_KEY, "touchpoint-anchor", id],
    queryFn: () =>
      api<ApprovalTouchpointAnchor>(`/admin/approvals/${id}/touchpoint-anchor`),
    staleTime: 10_000,
    enabled: enabled && id > 0,
    retry: 1,
  });
}

const BASE_KEY = ["admin", "approvals"] as const;
const LIST_KEY = [...BASE_KEY, "list"] as const;
const COUNT_KEY = [...BASE_KEY, "count"] as const;

function buildQueryString(filters: ApprovalFilters): string {
  const params = new URLSearchParams();
  if (filters.project) params.set("project", filters.project);
  if (filters.eventType) params.set("eventType", filters.eventType);
  if (filters.gateType) params.set("gateType", filters.gateType);
  const s = params.toString();
  return s ? `?${s}` : "";
}

export function useApprovals(filters: ApprovalFilters) {
  return useQuery<ApprovalListResponse>({
    queryKey: [...LIST_KEY, filters],
    queryFn: () =>
      api<ApprovalListResponse>(`/admin/approvals${buildQueryString(filters)}`),
    staleTime: 10_000,
  });
}

export function useApprovalHistory(filters: ApprovalHistoryFilters, offset: number) {
  return useQuery<ApprovalHistoryResponse>({
    queryKey: [...BASE_KEY, "history", filters, offset],
    placeholderData: keepPreviousData,
    queryFn: () =>
      api<ApprovalHistoryResponse>(
        `/admin/approvals/history${buildHistoryQuery(filters, HISTORY_PAGE_SIZE, offset)}`,
      ),
    staleTime: 10_000,
  });
}

export function useApprovalsCount(options?: { polling?: boolean }) {
  return useQuery<{ count: number }>({
    queryKey: COUNT_KEY,
    queryFn: () => api<{ count: number }>("/admin/approvals/count"),
    staleTime: 15_000,
    // Pause when tab not visible: refetchInterval as a function returns false
    // when document.hidden, so we don't burn API calls in background tabs.
    refetchInterval: options?.polling
      ? () => (typeof document !== "undefined" && document.hidden ? false : 30_000)
      : false,
    refetchIntervalInBackground: false,
  });
}

export function invalidateApprovals(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: BASE_KEY });
}

interface DecisionInput {
  id: number;
  action: "approve" | "reject";
  comment?: string;
  /** Only valid with action="approve" + merge_review gate. */
  droppedKeys?: string[];
}

export function useDecideMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, action, comment, droppedKeys }: DecisionInput) => {
      const body: Record<string, unknown> = {};
      if (comment) body["comment"] = comment;
      if (action === "approve" && droppedKeys && droppedKeys.length > 0) {
        body["mode"] = "drop_keys";
        body["droppedKeys"] = droppedKeys;
      }
      const hasBody = Object.keys(body).length > 0;
      return api<{ ok: true; id: number }>(
        `/admin/approvals/${id}/${action}`,
        {
          method: "POST",
          body: hasBody ? JSON.stringify(body) : undefined,
        },
      );
    },
    onMutate: async ({ id }) => {
      await qc.cancelQueries({ queryKey: LIST_KEY });
      const snapshots = qc.getQueriesData<ApprovalListResponse>({
        queryKey: LIST_KEY,
      });
      qc.setQueriesData<ApprovalListResponse>(
        { queryKey: LIST_KEY },
        (old) =>
          old
            ? {
                ...old,
                items: old.items.filter((it) => it.id !== id),
                total: Math.max(0, old.total - 1),
              }
            : old,
      );
      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      ctx?.snapshots.forEach(([key, value]) => {
        qc.setQueryData(key, value);
      });
    },
    onSettled: () => {
      invalidateApprovals(qc);
    },
  });
}

interface BulkInput {
  ids: number[];
  action: "approve" | "reject";
  comment?: string;
}

export function useBulkMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkInput) =>
      api<{
        ok: true;
        updatedCount: number;
        skippedCount: number;
        skipped: number[];
      }>("/admin/approvals/bulk", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onMutate: async ({ ids }) => {
      await qc.cancelQueries({ queryKey: LIST_KEY });
      const snapshots = qc.getQueriesData<ApprovalListResponse>({
        queryKey: LIST_KEY,
      });
      const idSet = new Set(ids);
      qc.setQueriesData<ApprovalListResponse>(
        { queryKey: LIST_KEY },
        (old) =>
          old
            ? {
                ...old,
                items: old.items.filter((it) => !idSet.has(it.id)),
                total: Math.max(0, old.total - ids.length),
              }
            : old,
      );
      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      ctx?.snapshots.forEach(([key, value]) => {
        qc.setQueryData(key, value);
      });
    },
    onSettled: () => {
      invalidateApprovals(qc);
    },
  });
}
